import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../config/const";
import "./Profile.css";

const Profile = () => {
  const [user, setUser] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    dateOfBirth: "",
    preferredName: "",
    location: "",
  });
  const [children, setChildren] = useState([]);
  const [childFormData, setChildFormData] = useState({
    name: "",
    dateOfBirth: "",
    profilePicture: null,  // Will store the file object
    previewUrl: "",       // Will store temporary preview URL
  });
  const [childEditIndex, setChildEditIndex] = useState(null);
  const email = localStorage.getItem("email");

  const [formErrors, setFormErrors] = useState({
    name: '',
    dateOfBirth: '',
    profilePicture: ''
  });

  const calculateAge = (dateString) => {
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const month = today.getMonth() - birthDate.getMonth();
    if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age + " years";
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("idToken");
        const response = await axios.get(`${config.backend_url}/user/user`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            email,
          },
        });
        setUser(response.data.user);
        setFormData({
          name: response.data.user.name,
          email: response.data.user.email,
          dateOfBirth: response.data.user.dateOfBirth
            ? response.data.user.dateOfBirth.split("T")[0]
            : "",
          preferredName: response.data.user.preferredName,
          location: response.data.user.location,
        });
        const childrenResponse = await axios.get(
          `${config.backend_url}/user/children`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              email,
            },
          }
        );
        setChildren(childrenResponse.data.children);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, [email]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChildInputChange = (e) => {
    const { name, value } = e.target;
    setChildFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (value.trim()) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("idToken");
      await axios.post(
        `${config.backend_url}/user/user/update`,
        { ...formData, children },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUser(formData);
      setEditMode(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const validateChildForm = () => {
    const errors = {};
    
    if (!childFormData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!childFormData.dateOfBirth) {
      errors.dateOfBirth = 'Date of Birth is required';
    }
    
    if (!childFormData.profilePicture) {
      errors.profilePicture = 'Profile Picture is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddChild = async () => {
    if (!validateChildForm()) {
      return;
    }
    try {
      // Create temporary child object for immediate display
      const tempChild = {
        name: childFormData.name,
        dateOfBirth: childFormData.dateOfBirth,
        profilePicture: childFormData.previewUrl,
        isCartoonifying: true,
        _id: `temp-${Date.now()}` // temporary ID
      };

      setChildren(prev => [...prev, tempChild]);

      let profilePictureUrl = null;
      if (childFormData.profilePicture) {
        const formData = new FormData();
        formData.append('file', childFormData.profilePicture);
        formData.append('upload_preset', 'storybuddy');
        formData.append('cloud_name', 'dijb37son');

        const uploadResponse = await axios.post(
          'https://api.cloudinary.com/v1_1/dijb37son/image/upload',
          formData
        );
        profilePictureUrl = uploadResponse.data.secure_url;
      }

      const response = await axios.post(`${config.backend_url}/user/children`, {
        email,
        name: childFormData.name,
        dateOfBirth: childFormData.dateOfBirth,
        profilePicture: profilePictureUrl
      });

      const cartoonResponse = await axios.post(`${config.backend_url}/user/cartoonify`, {
        email,
        childId: response.data.child._id
      });

      setChildren(prev => prev.map(child => 
        child._id === tempChild._id ? {
          ...response.data.child,
          cartoonImage: cartoonResponse.data.cartoonImage,
          isCartoonifying: false
        } : child
      ));

      setChildFormData({
        name: "",
        dateOfBirth: "",
        profilePicture: null,
        previewUrl: ""
      });
      setChildEditIndex(null);

    } catch (error) {
      console.error("Error adding child:", error);
      // Remove temporary child if error occurs
      setChildren(prev => prev.filter(child => child._id !== `temp-${Date.now()}`));
      alert("Failed to add child. Please try again.");
    }
  };

  const handleEditChild = (index) => {
    setChildFormData({
      ...children[index],
      dateOfBirth: formatDate(children[index].dateOfBirth),
    });
    setChildEditIndex(index);
  };

  const handleUpdateChild = async () => {
    await axios.put(`${config.backend_url}/user/children`, {
      email,
      _id: children[childEditIndex]._id,
      name: childFormData.name,
      dateOfBirth: childFormData.dateOfBirth,
    });

    const updatedChildren = children.map((child, i) =>
      i === childEditIndex ? childFormData : child
    );
    setChildren(updatedChildren);

    setChildFormData({
      name: "",
      dateOfBirth: "",
    });

    setChildEditIndex(null);
  };

  const handleDeleteChild = async (index) => {
    await axios.delete(`${config.backend_url}/user/children`, {
      data: {
        email,
        _id: children[index]._id,
      },
    });

    const updatedChildren = children.filter((child, i) => i !== index);
    setChildren(updatedChildren);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDate2 = (date) => {
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  };

  // Update handleProfilePicture to clear error on valid input
  const handleProfilePicture = (e) => {
    const file = e.target.files[0];
    if (file) {
      setChildFormData(prev => ({
        ...prev,
        profilePicture: file,
        previewUrl: URL.createObjectURL(file)
      }));
      // Clear profile picture error when valid file is selected
      setFormErrors(prev => ({
        ...prev,
        profilePicture: ''
      }));
    }
  };

  const handleCartoonify = async (index) => {
    try {
      
      const updatedChildren = [...children];
      updatedChildren[index] = {
        ...updatedChildren[index],
        isCartoonifying: true  
      };
      setChildren(updatedChildren);
  
      const response = await axios.post(`${config.backend_url}/user/cartoonify`, {
        email,
        childId: children[index]._id
      });
      
      
      const newChildren = [...children];
      newChildren[index] = {
        ...newChildren[index],
        cartoonImage: response.data.cartoonImage,
        isCartoonifying: false  
      };
      setChildren(newChildren);
      
    } catch (error) {
      console.error("Error cartoonifying image:", error);
      const newChildren = [...children];
      newChildren[index] = {
        ...newChildren[index],
        isCartoonifying: false
      };
      setChildren(newChildren);
      alert("Failed to cartoonify image. Please try again.");
    }
  };

  return (
    <div className="profile-container">
      <h1>User Profile</h1>
      {editMode ? (
        <form onSubmit={handleFormSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name:</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              readOnly
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              readOnly
            />
          </div>
          <div className="form-group">
            <label htmlFor="dateOfBirth">Date of Birth:</label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="location">Location:</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="age">Age:</label>
            <input
              type="text"
              id="age"
              name="age"
              value={calculateAge(formData.dateOfBirth)}
              readOnly
            />
          </div>
          <button type="submit">Save</button>
          <button type="button" onClick={() => setEditMode(false)}>
            Cancel
          </button>
        </form>
      ) : (
        <div className="profile-details">
          <p>
            <strong>Name:</strong> {user.name}
          </p>
          <p>
            <strong>Email:</strong> {user.email}
          </p>
          <p>
            <strong>Date of Birth:</strong>{" "}
            {user.dateOfBirth ? formatDate(user.dateOfBirth) : ""}
          </p>
          <p>
            <strong>Location:</strong> {user.location}
          </p>
          <p>
            <strong>Age: </strong> {calculateAge(user.dateOfBirth)}
          </p>
          <button onClick={() => setEditMode(true)}>Edit Profile</button>
        </div>
      )}
      <div className="children-section">
        <h2>Children</h2>
        <div className="children-list">
          {children.map((child, index) => (
            <div key={index} className="child-profile">
              <div className="child-section">
                {/* Left Side - Child Info */}
                <div className="child-info">
                  <div className="child-header">
                    <div className="profile-container">
                      <img 
                        src={child.profilePicture || 'https://via.placeholder.com/120'} 
                        alt={`${child.name}'s profile`}
                        className="child-profile-pic"
                      />
                      <div className="admin-actions">
                        {/* <button className="action-button" onClick={() => handleEditChild(index)}>
                          ✏️
                          <span className="tooltip">Edit</span>
                        </button> */}
                        <button className="action-button" onClick={() => handleDeleteChild(index)}>
                          🗑️
                          <span className="tooltip">Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="child-profile-details">
                    <p><strong>Name:</strong> {child.name}</p>
                    <p><strong>Age:</strong> {calculateAge(child.dateOfBirth)}</p>
                    {/* <button 
                      className="cartoonify-button"
                      onClick={() => handleCartoonify(index)}
                      disabled={child.isCartoonifying}
                    >
                      {child.isCartoonifying ? "Processing..." : "Cartoonify"}
                    </button> */}
                  </div>
                </div>

                {/* Right Side - Cartoon Images */}
                <div className="cartoon-info">
                  <h4>Cartoon Image</h4>
                  {child.isCartoonifying ? (
                    <div className="cartoon-loading">
                      <div className="loading-spinner"></div>
                      <p>Creating cartoon avatar...</p>
                    </div>
                  ) : child.cartoonImage ? (
                    <div className="cartoon-unit">
                      <img 
                        src={child.cartoonImage} 
                        alt={`${child.name}'s cartoon`}
                        className="cartoon-pic"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="cartoonify-feature-intro">
          <div className="feature-header">
            <span className="magic-icon">✨</span>
            <h3>Turn Your Child's Photo into a Cartoon Avatar!</h3>
          </div>
          <div className="feature-description">
            <p>When you add your child's photo, you can transform it into a unique cartoon character 
            that will appear in their personalized stories.</p>
            <div className="feature-benefits">
              <ul>
                <li>✓ Create unique cartoon avatars</li>
                <li>✓ Use in personalized stories</li>
                <li>✓ Make storytelling more engaging</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="childName">Child Name: <span className="required">*</span></label>
          <input
            type="text"
            id="childName"
            name="name"
            value={childFormData.name}
            onChange={handleChildInputChange}
            className={formErrors.name ? 'error' : ''}
          />
          {formErrors.name && <span className="error-message">{formErrors.name}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="childDateOfBirth">Child Date of Birth: <span className="required">*</span></label>
          <input
            type="date"
            id="childDateOfBirth"
            name="dateOfBirth"
            value={formatDate2(childFormData.dateOfBirth)}
            onChange={handleChildInputChange}
            className={formErrors.dateOfBirth ? 'error' : ''}
          />
          {formErrors.dateOfBirth && <span className="error-message">{formErrors.dateOfBirth}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="childAge">Child Age:</label>
          <input
            type="text"
            id="childAge"
            name="childAge"
            value={calculateAge(childFormData.dateOfBirth)}
            readOnly
          />
        </div>
        <div className="form-group">
          <label htmlFor="childProfilePicture">Profile Picture: <span className="required">*</span></label>
          <input
            type="file"
            id="childProfilePicture"
            accept="image/*"
            onChange={handleProfilePicture}
            className={formErrors.profilePicture ? 'error' : ''}
          />
          {formErrors.profilePicture && <span className="error-message">{formErrors.profilePicture}</span>}
          {childFormData.previewUrl && (
            <div className="profile-preview">
              <img 
                src={childFormData.previewUrl} 
                alt="Preview" 
              />
              <button 
                type="button"
                className="remove-image" 
                onClick={() => {
                  setChildFormData(prev => ({
                    ...prev,
                    profilePicture: null,
                    previewUrl: ""
                  }));
                  // Add error back when image is removed
                  setFormErrors(prev => ({
                    ...prev,
                    profilePicture: 'Profile Picture is required'
                  }));
                }}
              >
                ×
              </button>
            </div>
          )}
        </div>
        {childEditIndex !== null ? (
          <button type="button" onClick={handleUpdateChild}>
            Update Child
          </button>
        ) : (
          <button type="button" onClick={handleAddChild}>
            Add Child
          </button>
        )}
      </div>
    </div>
  );
};

export default Profile;
